import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  type?: string;
  jsonLd?: object | object[];
  keywords?: string;
}

const BASE_URL = "https://wittmann-group.ro";

const DEFAULT_KEYWORDS = "masini de injectie, injectie plastic, termoregulatoare, roboti, roboti industriali, robot plastice, mase plastice, uscator plastic, WITTMANN, echipamente plastic";

export default function SEOHead({ title, description, path, type = "website", jsonLd, keywords }: SEOHeadProps) {
  const { lang } = useLanguage();
  const canonicalUrl = `${BASE_URL}${path}`;
  const fullTitle = path === "/" ? title : `${title} | WITTMANN ROMANIA`;

  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "WITTMANN ROMANIA",
    url: BASE_URL,
    logo: `${BASE_URL}/favicon.jpeg`,
    description: "Soluții complete pentru industria de prelucrare a maselor plastice",
    address: [
      {
        "@type": "PostalAddress",
        streetAddress: "Bulevardul Iuliu Maniu 7, corp C",
        addressLocality: "București",
        postalCode: "061102",
        addressCountry: "RO",
      },
      {
        "@type": "PostalAddress",
        streetAddress: "Strada Zorelelor 12",
        addressLocality: "Oradea",
        postalCode: "410583",
        addressCountry: "RO",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+40720227255",
        contactType: "sales",
      },
      {
        "@type": "ContactPoint",
        telephone: "+40725227255",
        contactType: "sales",
      },
    ],
    sameAs: ["https://www.youtube.com/@WittmannRomania", "https://www.wittmann-group.com"],
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || DEFAULT_KEYWORDS} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang – distinct URLs per language */}
      <link rel="alternate" hrefLang="ro" href={`${BASE_URL}${path}${path.includes("?") ? "&" : "?"}lang=ro`} />
      <link rel="alternate" hrefLang="en" href={`${BASE_URL}${path}${path.includes("?") ? "&" : "?"}lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="WITTMANN ROMANIA" />
      <meta property="og:image" content={`${BASE_URL}/favicon.jpeg`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* JSON-LD */}
      {Array.isArray(jsonLd) ? (
        jsonLd.map((item, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd || defaultJsonLd)}
        </script>
      )}
    </Helmet>
  );
}
